import { useEffect, useState } from "react";
import { Bell, X, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { enablePush, getPushStatus, pushSupported } from "@/lib/push";

const DISMISS_KEY = "grindstugatan_push_dismissed_at";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 dagar

export function PushPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Cooldown — visa inte om dismissad nyligen
        const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (Date.now() - dismissedAt < COOLDOWN_MS) return;

        const supported = await pushSupported();
        if (!supported) return;

        const s = await getPushStatus();
        if (cancelled) return;
        setStatus(s);

        // Visa bara om allt är klart men användaren inte har aktiverat än
        if (
          s.supported &&
          s.firebase_configured &&
          s.vapid_configured &&
          s.permission !== "granted" &&
          s.permission !== "denied" &&
          !localStorage.getItem("grindstugatan_push_token")
        ) {
          // 5 sek delay så hemskärmen hinner ladda först
          setTimeout(() => !cancelled && setOpen(true), 5000);
        }
      } catch (_) {
        // Tyst — push är opt-in, inget ska blocka appen
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push-notiser aktiverade! 🔔");
      setOpen(false);
    } catch (e) {
      const msg = e?.message || "Kunde inte aktivera push-notiser";
      if (msg.includes("nekat")) {
        toast.error("Tillstånd nekat. Aktivera notiser i webbläsarinställningarna och försök igen.");
        dismiss();
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center bg-black/50 p-3"
      onClick={dismiss}
      data-testid="push-prompt-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-5 fade-up"
        data-testid="push-prompt"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--moss)] text-white flex items-center justify-center flex-shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-medium">Få notiser?</h3>
            <p className="text-sm text-[color:var(--text-secondary)] mt-0.5">
              Vi pingar dig när det händer något viktigt i familjen.
            </p>
          </div>
          <button
            onClick={dismiss}
            className="rounded-lg p-1 hover:bg-[color:var(--bg-elevated)]"
            data-testid="push-prompt-close"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </div>

        <ul className="space-y-2 mt-4 mb-5">
          <li className="flex items-start gap-2 text-sm text-[color:var(--text-secondary)]">
            <CheckCircle2 size={14} className="text-[color:var(--moss)] flex-shrink-0 mt-0.5" />
            <span>Nya läxor, prov och schemaändringar i Vklass</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-[color:var(--text-secondary)]">
            <CheckCircle2 size={14} className="text-[color:var(--moss)] flex-shrink-0 mt-0.5" />
            <span>Mail och poster från Classroom</span>
          </li>
          <li className="flex items-start gap-2 text-sm text-[color:var(--text-secondary)]">
            <Sparkles size={14} className="text-[color:var(--jarvis)] flex-shrink-0 mt-0.5" />
            <span>Söndagskvällens veckosammanfattning från Edit</span>
          </li>
        </ul>

        <button
          onClick={activate}
          disabled={busy}
          className="w-full h-11 rounded-xl bg-[color:var(--moss)] text-white text-sm font-medium hover:bg-[#1f421f] disabled:opacity-60 mb-2"
          data-testid="push-prompt-activate"
        >
          {busy ? "Aktiverar..." : "Aktivera push-notiser"}
        </button>
        <button
          onClick={dismiss}
          className="w-full h-10 rounded-xl text-sm text-[color:var(--text-tertiary)] hover:bg-[color:var(--bg-elevated)]"
          data-testid="push-prompt-skip"
        >
          Inte nu
        </button>
      </div>
    </div>
  );
}

export default PushPrompt;
